import { OpenAIProvider } from './providers/openai';
import { supabase } from '../supabase';
import { ProcessingOptions } from './types';
import { debug, debugGroup } from '../debug';
import { defaultConfig } from './config';
import { getErrorMessage } from '../type-fixes';

export class AIProcessor {
  private providers: Map<string, any> = new Map();
  private defaultProvider: string | null = null;

  async initialize() {
    debug('AI_PROCESSOR', 'Initializing AI Processor');
    
    try {
      // Get active AI providers from database
      const { data: providers, error } = await supabase
        .from('ai_providers')
        .select('*')
        .eq('is_active', true);

      if (error) {
        debug('AI_PROCESSOR', 'Error fetching providers:', error);
        throw error;
      }

      debugGroup('AI_PROCESSOR', 'Found providers', () => {
        console.log('Count:', providers?.length || 0);
        console.log('Providers:', providers);
      });

      // Initialize providers
      for (const provider of providers || []) {
        debug('AI_PROCESSOR', `Initializing provider: ${provider.provider_type}`);
        
        try {
          const openaiProvider = new OpenAIProvider(
            provider.api_key || defaultConfig.apiKey,
            provider.config
          );
          this.providers.set(provider.id, openaiProvider);
          
          // Set as default if first provider
          if (!this.defaultProvider) {
            this.defaultProvider = provider.id;
          }
          debug('AI_PROCESSOR', `Successfully initialized provider: ${provider.id}`);
        } catch (error) {
          debug('AI_PROCESSOR', `Failed to initialize provider ${provider.id}:`, error);
        }
      }

      debug('AI_PROCESSOR', 'Initialization complete', {
        providersCount: this.providers.size,
        defaultProvider: this.defaultProvider
      });
    } catch (error) {
      debug('AI_PROCESSOR', 'Initialization failed:', error);
      throw error;
    }
  }

  async processSurvey(surveyCode: string, options: ProcessingOptions = {}) {
    debugGroup('AI_PROCESSOR', 'Processing survey', () => {
      console.log('Survey Code:', surveyCode);
      console.log('Options:', options);
      console.log('Available Providers:', Array.from(this.providers.keys()));
      console.log('Default Provider:', this.defaultProvider);
    });

    let insight;
    try {
      // Create insight record
      const { data: newInsight, error: insertError } = await supabase
        .from('survey_insights')
        .insert({
          survey_code: surveyCode,
          provider_id: options.provider || this.defaultProvider,
          status: 'processing',
          processing_started_at: new Date().toISOString()
        })
        .select()
        .single();

      if (insertError) {
        debug('AI_PROCESSOR', 'Error creating insight record:', insertError);
        throw insertError;
      }

      insight = newInsight;
      debug('AI_PROCESSOR', 'Created insight record:', insight.id);

      // Get provider
      const providerId = options.provider || this.defaultProvider;
      if (!providerId) {
        throw new Error('No AI provider available');
      }

      const provider = this.providers.get(providerId);
      if (!provider) {
        debug('AI_PROCESSOR', 'Provider not found:', providerId);
        throw new Error('AI provider not found');
      }

      debug('AI_PROCESSOR', 'Using provider:', providerId);

      // Process survey
      debug('AI_PROCESSOR', 'Starting survey processing');
      const results = await provider.processSurveyResults(surveyCode, options);

      // Update insight record
      const { error: updateError } = await supabase
        .from('survey_insights')
        .update({
          status: 'completed',
          insights: results,
          processing_completed_at: new Date().toISOString()
        })
        .eq('id', insight.id);

      if (updateError) {
        debug('AI_PROCESSOR', 'Error updating insight record:', updateError);
        throw updateError;
      }

      debug('AI_PROCESSOR', 'Processing completed successfully');
      return results;
    } catch (error) {
      debug('AI_PROCESSOR', 'Error processing survey:', error);

      // Update insight record with error
      if (insight?.id) {
        debug('AI_PROCESSOR', 'Updating insight record with error');
        await supabase
          .from('survey_insights')
          .update({
            status: 'failed',
            error_message: getErrorMessage(error),
            processing_completed_at: new Date().toISOString()
          })
          .eq('id', insight.id);
      }

      throw error;
    }
  }
}